import { useState } from "react";
import { http, apiError } from "@/lib/api";
import { ArrowRight, ArrowLeft, Heart } from "@phosphor-icons/react";

const MOTIVATIONS = [
  "More confidence", "More energy", "Better relationship", "Better father",
  "Better husband/partner", "Better mental health", "More attractive",
  "Better sex life", "More self-respect", "Better performance at work",
  "Comfortable in photos", "Comfortable at the beach",
];

export default function GoalsStep({ initial, onBack, onNext, setErr }) {
  const [cw, setCw] = useState(initial?.weight_kg || "");
  const [gw, setGw] = useState("");
  const [cwaist, setCwaist] = useState(initial?.waist_cm || "");
  const [gwaist, setGwaist] = useState("");
  const [tgt12, setTgt12] = useState("");  // keeps internal name; field now means "kg to lose in chosen timeframe"
  const [timeframe, setTimeframe] = useState("");  // user-picked timeframe label (3mo / 6mo / 12mo / etc.)
  const [biggestStop, setBiggestStop] = useState("");
  const [biggestFrust, setBiggestFrust] = useState("");
  const [missed, setMissed] = useState("");
  const [noChange, setNoChange] = useState("");
  const [success, setSuccess] = useState("");
  const [drivingForce, setDrivingForce] = useState("");
  const [motivations, setMotivations] = useState([]);
  const [other, setOther] = useState("");
  const [whyNow, setWhyNow] = useState("");
  const [whyNot6mo, setWhyNot6mo] = useState("");
  const [whyNotNextYear, setWhyNotNextYear] = useState("");
  const [different, setDifferent] = useState("");
  const [commitment, setCommitment] = useState(8);
  const [allIn, setAllIn] = useState(null); // null | true | false
  const [saving, setSaving] = useState(false);

  const toggle = (m) => setMotivations((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);

  const save = async () => {
    if (!cw || !gw || !cwaist || !gwaist || !tgt12) { setErr("All numeric fields required."); return; }
    if (!timeframe) { setErr("Pick a realistic timeframe to lose the weight."); return; }
    if (allIn === null) { setErr("Please answer the final question — are you all in?"); return; }
    setSaving(true); setErr("");
    try {
      await http.post("/client/goals", {
        current_weight_kg: Number(cw), goal_weight_kg: Number(gw),
        current_waist_cm: Number(cwaist), goal_waist_cm: Number(gwaist),
        target_loss_12mo_kg: Number(tgt12),
        target_loss_timeframe: timeframe,
        biggest_stop: biggestStop, biggest_frustration: biggestFrust,
        missed_out_on: missed, no_change_outcome: noChange, success_outcome: success,
        main_driving_force: drivingForce,
        motivations, motivations_other: other,
        why_now: whyNow, why_not_6mo: whyNot6mo, why_not_next_year: whyNotNextYear,
        what_makes_different: different,
        commitment: Number(commitment), all_in: !!allIn,
      });
      onNext();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="onboarding-step-goals">
      <div className="id-label mb-3">— Step 3 of 6 —</div>
      <h1 className="id-h1 mb-3">Your goals & why</h1>
      <p className="text-zinc-400 font-manrope mb-8">No fluff. The clearer you are now, the harder it is to quit on yourself later.</p>

      <div className="id-card mb-5">
        <div className="id-label mb-3">Numbers</div>
        <div className="grid grid-cols-2 gap-3">
          <Num label="Current weight (kg)" value={cw} set={setCw} testid="goal-cw" />
          <Num label="Goal weight (kg)" value={gw} set={setGw} testid="goal-gw" />
          <Num label="Current waist (cm)" value={cwaist} set={setCwaist} testid="goal-cwaist" />
          <Num label="Goal waist (cm)" value={gwaist} set={setGwaist} testid="goal-gwaist" />
          <Num label="Realistic kg you want to lose" value={tgt12} set={setTgt12} testid="goal-loss" full />
        </div>

        <div className="mt-4">
          <label className="id-label block mb-2">In what timeframe do you want to lose this weight?</label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="goal-timeframe">
            {[
              { v: "3mo", l: "3 months" },
              { v: "6mo", l: "6 months" },
              { v: "9mo", l: "9 months" },
              { v: "12mo", l: "12 months" },
              { v: "18mo", l: "18+ months" },
            ].map((opt) => (
              <button key={opt.v} type="button" onClick={() => setTimeframe(opt.v)} data-testid={`goal-timeframe-${opt.v}`}
                className={`py-3 border uppercase tracking-[0.15em] text-xs font-bold transition-colors ${timeframe === opt.v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                {opt.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="id-card mb-5 space-y-4">
        <div className="id-label">The real questions</div>
        <Long label="What's the biggest thing your weight is currently stopping you from doing?" value={biggestStop} set={setBiggestStop} testid="goal-stop" />
        <Long label="What frustrates you most about your health right now?" value={biggestFrust} set={setBiggestFrust} testid="goal-frust" />
        <Long label="What have you missed out on because of your fitness, confidence or weight?" value={missed} set={setMissed} testid="goal-missed" />
        <Long label="If nothing changes and you're in exactly the same position 6 months from now — how would that make you feel?" value={noChange} set={setNoChange} testid="goal-nochange" />
        <Long label="If you successfully lose the weight and keep it off, what changes?" value={success} set={setSuccess} testid="goal-success" />
      </div>

      <div className="id-card mb-5">
        <div className="id-label mb-1">Your main driving force</div>
        <p className="text-zinc-500 text-xs font-manrope mb-3">
          What is your <span className="text-white">main</span> driving force in wanting to change your life and drop weight and get back into shape? <span className="text-zinc-600">e.g. kids, family, confidence, health scare, wedding, feeling like myself again</span>
        </p>
        <textarea
          value={drivingForce}
          onChange={(e) => setDrivingForce(e.target.value)}
          rows={3}
          maxLength={500}
          className="id-input"
          placeholder="The one thing that's going to keep you going when it gets hard..."
          data-testid="goal-driving-force"
        />
      </div>

      <div className="id-card mb-5">
        <div className="id-label mb-1">What gets better when you win?</div>
        <p className="text-zinc-500 text-xs font-manrope mb-3">Tick all that apply.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="goal-motivations">
          {MOTIVATIONS.map((m) => {
            const on = motivations.includes(m);
            return (
              <button key={m} type="button" onClick={() => toggle(m)} data-testid={`goal-mot-${m.replace(/[^a-z]/gi, "").toLowerCase()}`}
                className={`text-left px-4 py-3 border text-sm font-manrope transition-colors ${on ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-300 hover:border-zinc-500"}`}>
                <span className={`inline-block w-3 h-3 mr-3 border ${on ? "bg-[#FF3333] border-[#FF3333]" : "border-zinc-500"}`}></span>
                {m}
              </button>
            );
          })}
        </div>
        <div className="mt-3">
          <label className="id-label block mb-2">Other</label>
          <input value={other} onChange={(e) => setOther(e.target.value)} className="id-input" placeholder="(optional)" data-testid="goal-mot-other" />
        </div>
      </div>

      <div className="id-card mb-5 space-y-4">
        <div className="id-label">Why now?</div>
        <Long label="Why is now the time?" value={whyNow} set={setWhyNow} testid="goal-whynow" />
        <Long label="Why not 6 months ago?" value={whyNot6mo} set={setWhyNot6mo} testid="goal-why6" />
        <Long label="Why not next year?" value={whyNotNextYear} set={setWhyNotNextYear} testid="goal-whynextyr" />
        <Long label="What makes this attempt different?" value={different} set={setDifferent} testid="goal-different" />
      </div>

      <div className="id-card mb-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="id-label">Commitment</div>
            <div className="text-zinc-500 text-xs font-manrope">1 = barely / 10 = absolute</div>
          </div>
          <div className="font-oswald text-4xl text-[#FF3333] font-bold" data-testid="goal-commitment-value">{commitment}</div>
        </div>
        <input type="range" min={1} max={10} step={1} value={commitment} onChange={(e) => setCommitment(Number(e.target.value))} className="w-full accent-[#FF3333]" data-testid="goal-commitment-slider" />
        <div className="flex justify-between text-[10px] text-zinc-600 uppercase tracking-[0.15em] mt-1"><span>1</span><span>10</span></div>
      </div>

      <div className="id-card mb-8 border-[#FF3333]">
        <Heart size={24} weight="fill" className="text-[#FF3333] mb-3" />
        <div className="id-label mb-3 text-[#FF3333]">— Final question —</div>
        <p className="text-zinc-200 font-manrope leading-relaxed mb-2">I can't do the work for you.</p>
        <p className="text-zinc-200 font-manrope leading-relaxed mb-2">I can't make you train. I can't make you walk.</p>
        <p className="text-zinc-200 font-manrope leading-relaxed mb-2">I can't make you choose the better option at dinner.</p>
        <p className="text-zinc-100 font-manrope leading-relaxed mb-6">But if you're willing to show up and do your part, <span className="text-[#FF3333] font-semibold">I'll be all in with you.</span></p>
        <div className="font-oswald text-2xl uppercase tracking-wide mb-4">Are you all in?</div>
        <div className="grid grid-cols-1 gap-2" data-testid="goal-allin">
          <button type="button" onClick={() => setAllIn(true)} data-testid="goal-allin-yes"
            className={`text-left px-5 py-4 border font-manrope text-sm transition-colors ${allIn === true ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-300 hover:border-zinc-500"}`}>
            <span className={`inline-block w-3 h-3 mr-3 border ${allIn === true ? "bg-[#FF3333] border-[#FF3333]" : "border-zinc-500"}`}></span>
            <span className="font-bold">YES.</span> I'm ready to stop starting over.
          </button>
          <button type="button" onClick={() => setAllIn(false)} data-testid="goal-allin-no"
            className={`text-left px-5 py-4 border font-manrope text-sm transition-colors ${allIn === false ? "border-zinc-500 bg-zinc-900 text-white" : "border-[#262626] text-zinc-300 hover:border-zinc-500"}`}>
            <span className={`inline-block w-3 h-3 mr-3 border ${allIn === false ? "bg-zinc-500 border-zinc-500" : "border-zinc-500"}`}></span>
            <span className="font-bold">NO.</span> I'm not ready to commit yet.
          </button>
        </div>
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="id-btn-secondary" data-testid="goal-back"><ArrowLeft size={14} weight="bold" /> Back</button>
        <button onClick={save} disabled={saving} className="id-btn-primary" data-testid="goal-next">
          {saving ? "Saving..." : "Continue"} <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function Num({ label, value, set, testid, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="id-label block mb-2">{label}</label>
      <input type="number" step={0.1} min={0} value={value} onChange={(e) => set(e.target.value)} className="id-input" data-testid={testid} />
    </div>
  );
}

function Long({ label, value, set, testid }) {
  return (
    <div>
      <label className="id-label block mb-2">{label}</label>
      <textarea value={value} onChange={(e) => set(e.target.value)} rows={2} className="id-input" data-testid={testid} />
    </div>
  );
}
